import {
  MotifButton,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
} from "@ey-xd/motif-react";
import { useEffect, useMemo, useState } from "react";
import { parseDateOnly, toDateOnly } from "../../../../utils/dateFunction";
import CompletionConfirmationStep from "./components/CompletionConfirmationStep";
import CompletionStep from "./components/CompletionStep";
import InProgressStep from "./components/InProgressStep";
import useProvisionBillingPreview from "./useProvisionBillingPreview";
import { ProvisionDisplayDetails, SkuProvisionModalProps } from "./types";
import "./SkuProvisionModal.scss";

const COMPLETED_STATUS = "ordersku_active";

const toDisplayValue = (value: string): string => {
  if (!value) return "";
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const getSkuType = (
  selectedSkuData: SkuProvisionModalProps["selectedSkuData"],
): string => {
  const skuDetail = selectedSkuData.OrderDetails?.flatMap(
    (order) => order.SkuDetails,
  ).find((sku) => sku.SkuId === selectedSkuData.SkuId);
  if (skuDetail?.SkuType) return toDisplayValue(skuDetail.SkuType);
  return selectedSkuData.CostMetricCd?.toLowerCase().startsWith("fix")
    ? "Fixed"
    : "Variable";
};

const getInterval = (costInterval: string): string => {
  switch (costInterval?.toLowerCase()) {
    case "week":
      return "Weekly";
    case "month":
      return "Monthly";
    case "onetime":
      return "One Time";
    default:
      return toDisplayValue(costInterval);
  }
};

const SkuProvisionModal = ({
  show,
  provisionStatus,
  selectedSkuData,
  cancelHandler,
  saveStatus,
  dateHandler,
}: SkuProvisionModalProps) => {
  const isCompletion = provisionStatus === COMPLETED_STATUS;
  const [step, setStep] = useState<"selection" | "confirmation">("selection");
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    parseDateOnly(selectedSkuData.ProvisionDtm),
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  const {
    status: previewStatus,
    data: preview,
    error: previewError,
    requestedDate,
    requestPreview,
    resetPreview,
  } = useProvisionBillingPreview();

  const dateOnly = useMemo(() => toDateOnly(selectedDate), [selectedDate]);
  const skuType = useMemo(() => getSkuType(selectedSkuData), [selectedSkuData]);
  const interval = useMemo(
    () => getInterval(selectedSkuData.CostIntervalCd),
    [selectedSkuData.CostIntervalCd],
  );
  const details = useMemo<ProvisionDisplayDetails>(
    () => ({
      order: `${selectedSkuData.Description} (${selectedSkuData.OrderParentId})`,
      sku: `${selectedSkuData.Name} (${selectedSkuData.SkuId})`,
      skuType,
      interval,
    }),
    [interval, selectedSkuData, skuType],
  );

  const previewRequest = useMemo(
    () => ({
      provisionCompletionDate: dateOnly,
      skuType,
      costInterval: selectedSkuData.CostIntervalCd,
      orderId: selectedSkuData.OrderParentId,
      skuId: selectedSkuData.SkuId,
    }),
    [
      dateOnly,
      selectedSkuData.CostIntervalCd,
      selectedSkuData.OrderParentId,
      selectedSkuData.SkuId,
      skuType,
    ],
  );

  useEffect(() => {
    if (!show || !isCompletion || !dateOnly) {
      resetPreview();
      return;
    }

    requestPreview(previewRequest);
  }, [
    dateOnly,
    isCompletion,
    previewRequest,
    requestPreview,
    resetPreview,
    show,
  ]);

  const submitStatus = async () => {
    if (!selectedDate || !dateOnly) return;

    dateHandler(parseDateOnly(dateOnly), selectedSkuData);
    setIsSaving(true);
    try {
      await saveStatus(provisionStatus, dateOnly, selectedSkuData);
    } finally {
      setIsSaving(false);
    }
  };

  const canContinue =
    requestedDate === dateOnly &&
    ((previewStatus === "succeeded" && preview !== null) ||
      previewStatus === "failed");

  let modalTitle = "Mark SKU provision as In progress";
  let primaryButtonLabel = "Mark as In progress";
  let modalContent = (
    <InProgressStep
      details={details}
      selectedDate={selectedDate}
      onDateChange={setSelectedDate}
    />
  );

  if (isCompletion) {
    modalTitle = "Complete SKU Provision";
    primaryButtonLabel = "Continue";
    modalContent = (
      <CompletionStep
        details={details}
        selectedDate={selectedDate}
        preview={preview}
        previewStatus={previewStatus}
        previewError={previewError}
        onDateChange={setSelectedDate}
      />
    );
  }

  if (step === "confirmation") {
    modalTitle = "Confirm SKU completion";
    primaryButtonLabel = "Mark as complete";
    modalContent = (
      <CompletionConfirmationStep
        details={details}
        provisionCompletionDate={dateOnly}
        preview={preview}
      />
    );
  }

  const modalClassName =
    step === "confirmation"
      ? "sku-provision-modal sku-provision-modal--confirmation"
      : "sku-provision-modal";
  const closeModal = () => {
    cancelHandler();
    document.body.style.overflow = "";
  };
  const secondaryButtonLabel = step === "confirmation" ? "Back" : "Cancel";
  const handleSecondaryAction =
    step === "confirmation" ? () => setStep("selection") : closeModal;
  const handlePrimaryAction =
    !isCompletion || step === "confirmation"
      ? submitStatus
      : () => setStep("confirmation");
  const isPrimaryButtonDisabled =
    isSaving ||
    !dateOnly ||
    (isCompletion && step === "selection" && !canContinue);

  return (
    <MotifModal
      size="md"
      show={show}
      onClose={closeModal}
      className={modalClassName}
    >
      <MotifModalHeader>{modalTitle}</MotifModalHeader>
      <MotifModalBody>
        <div className="sku-provision-modal__content">{modalContent}</div>
      </MotifModalBody>
      <MotifModalFooter>
        <div className="sku-provision-actions">
          <MotifButton
            type="button"
            variant="secondary"
            disabled={isSaving}
            onClick={handleSecondaryAction}
          >
            {secondaryButtonLabel}
          </MotifButton>
          <MotifButton
            type="button"
            disabled={isPrimaryButtonDisabled}
            onClick={handlePrimaryAction}
          >
            {primaryButtonLabel}
          </MotifButton>
        </div>
      </MotifModalFooter>
    </MotifModal>
  );
};

export default SkuProvisionModal;
